import { createFileRoute, useNavigate } from "@tanstack/react-router";
export const Route = createFileRoute("/admin/_layout/users")({
  component: UserDataTable,
});

import { useState, useEffect, useMemo, useCallback } from "react";

import { UserTable } from "@/components/crud/table/UserTable";
import { UserFormSheet } from "@/components/crud/forms/UserFormSheet";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { SidebarInset } from "@/components/ui/sidebar";

import { DashboardHeader } from "@/components/dashboard-header";
import useFetchUser, { type UserOrdering } from "@/hooks/api/user/useFetchUser";
import type { ApiUser } from "@/types/shift-assignment";
import type { CreateUserPayload } from "@/types/user";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import useCreateUser from "@/hooks/api/user/useCreateUser";
import { toast } from "sonner";
import useDeactivateUser from "@/hooks/api/user/useDeactivateUser";
import useReactivateUser from "@/hooks/api/user/useReactivateUser";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import PageHeader from "@/components/reusable-components/header/page-header";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import SelectUser from "@/components/reusable-components/select-user";
import { Controller, useForm } from "react-hook-form";
import useAutoCompleteUser from "@/hooks/api/user/useAutoCompleteUser";
import { useDebounce } from "@/hooks/use-debounce";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function UserDataTable() {
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, resetDebouncedSearch] = useDebounce(searchQuery, 500);

  //Filters

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        first_name: SortDirection;
        last_name: SortDirection;
        date_joined: SortDirection;
        is_active: SortDirection;
      },
      UserOrdering
    >({
      first_name: null,
      last_name: null,
      date_joined: null,
      is_active: null,
    });
  // UI States Formsheet
  // Add / edit / deactivate are users.account.manage on the server; users
  // are deactivated, never deleted.
  // the page itself opens with users.profile.view, so hide what would 403.
  const { data: capabilities } = useMyPermissions();
  const canManageAccounts = can(capabilities, "users.account.manage");
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [userToBlockUnblock, setUserToBlockUnblock] = useState<ApiUser | null>(
    null
  );
  const [isBlockUnblockDialogOpen, setIsBlockUnblockDialogOpen] =
    useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const { setValue, watch, control } = useForm<{ user_id?: number }>({
    defaultValues: { user_id: undefined },
  });

  const watchedUserId = watch("user_id");

  // For resetting after create
  const resetFilters = () => {
    setSearchQuery("");
    resetDebouncedSearch();
    setOrdering({
      first_name: null,
      last_name: null,
      date_joined: null,
      is_active: null,
    });
    setPage(1);
    // Clear selected user filter
    setValue("user_id", undefined);
  };

  const usersQuery = useFetchUser({
    page_size: pageSize,
    page,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
  });
  const users = usersQuery.data?.results ?? [];
  const count = usersQuery.data?.count ?? 0;

  const createUser = useCreateUser();
  const deactivateUser = useDeactivateUser();
  const reactivateUser = useReactivateUser();

  const [autoCompleteSearch, setAutoCompleteSearch] = useState("");
  const autoCompleteQuery = useAutoCompleteUser({
    search: autoCompleteSearch || undefined,
  });
  const autoUsers = useMemo(
    () => autoCompleteQuery.data?.pages.flatMap((p) => p.results) ?? [],
    [autoCompleteQuery.data],
  );
  const autoCompleteLoading = autoCompleteQuery.isFetching;

  const isMutating = createUser.isPending;
  const isLoading = usersQuery.isPending || isMutating;
  const isFetching = usersQuery.isFetching || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, setPage]);

  // Handlers
  const handleBlockUnblockClick = (user: ApiUser) => {
    setUserToBlockUnblock(user);
    setIsBlockUnblockDialogOpen(true);
  };

  const isDeactivating = userToBlockUnblock?.is_active ?? true;

  const handleBlockUnblockConfirm = async () => {
    if (!userToBlockUnblock) return;
    try {
      if (isDeactivating) {
        const result = await deactivateUser.mutateAsync({
          id: userToBlockUnblock.id,
        });
        const cancelled = result.cancelled_pending_requests;
        const cancelledParts = [
          cancelled?.leave_requests
            ? `${cancelled.leave_requests} pending leave request${cancelled.leave_requests === 1 ? "" : "s"}`
            : null,
          cancelled?.overtime_requests
            ? `${cancelled.overtime_requests} pending overtime request${cancelled.overtime_requests === 1 ? "" : "s"}`
            : null,
        ].filter(Boolean);
        toast.success("User deactivated successfully!", {
          description: cancelledParts.length
            ? `Cancelled ${cancelledParts.join(" and ")}.`
            : undefined,
        });
        // T-115: a deactivated custodian's open manifests can only move via
        // Reassign -- say so now rather than leave HR to find them stuck.
        const manifests = result.open_manifests_needing_reassignment ?? [];
        if (manifests.length > 0) {
          toast.warning(
            `${`${userToBlockUnblock.first_name} ${userToBlockUnblock.last_name}`.trim() || userToBlockUnblock.email} still holds ${manifests.length} open deployment manifest${manifests.length === 1 ? "" : "s"}`,
            {
              description: `${manifests.map((m) => m.manifest_number).join(", ")} - reassign the custodian on each so the kit can be returned.`,
              duration: Infinity,
              closeButton: true,
              action: {
                label: manifests.length === 1 ? "Open" : "Open first",
                onClick: () =>
                  navigate({
                    to: "/admin/inventory/deployment-manifests/$id",
                    params: { id: String(manifests[0].id) },
                  }),
              },
            },
          );
        }
      } else {
        await reactivateUser.mutateAsync({ id: userToBlockUnblock.id });
        toast.success("User reactivated successfully!");
      }
      setIsBlockUnblockDialogOpen(false);
      setUserToBlockUnblock(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  // Pagination

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / pageSize) : 1;
  }, [count, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleSearchUsers = useCallback((query: string) => {
    setAutoCompleteSearch(query);
  }, []);

  const navigate = useNavigate();

  const handleView = (user: ApiUser) => {
    navigate({ to: "/admin/users/$id", params: { id: String(user.id) } });
  };

  const handleEdit = (user: ApiUser) => {
    navigate({
      to: "/admin/users/$id",
      params: { id: String(user.id) },
      search: { tab: "profile", edit: true },
    });
  };

  const handleNameClick = (user: ApiUser) => {
    navigate({
      to: "/admin/users/$id",
      params: { id: String(user.id) },
    });
  };

  const handleCreate = () => {
    setIsSheetOpen(true);
  };

  const onCreate = async (data: CreateUserPayload) => {
    const created = await createUser.mutateAsync(data);
    toast.success("User created successfully");
    setIsSheetOpen(false);
    resetFilters();
    return created;
  };
  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        {/* Header */}
        <PageHeader
          title="Users"
          description="Manage your users and their details here."
          onAdd={canManageAccounts ? handleCreate : undefined}
          addLabel="Add User"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <Controller
            name="user_id"
            control={control}
            render={() => (
              <SelectUser
                watchedUserId={watchedUserId}
                users={autoUsers}
                onSearchUsers={handleSearchUsers}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                setValue={setValue}
                isLoading={autoCompleteLoading}
                fetchNextPage={autoCompleteQuery.fetchNextPage}
                hasMore={autoCompleteQuery.hasNextPage}
              />
            )}
          />
        </FiltersPanel>

        {/* User Table */}
        <UserTable
          users={users}
          onView={handleView}
          onEdit={canManageAccounts ? handleEdit : undefined}
          onBlockUnblock={canManageAccounts ? handleBlockUnblockClick : undefined}
          onNameClick={handleNameClick}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
        />

        {/* Pagination */}
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        {/* Deactivate/Reactivate Confirmation Dialog */}
        <DeleteConfirmDialog
          isOpen={isBlockUnblockDialogOpen}
          onClose={() => setIsBlockUnblockDialogOpen(false)}
          onConfirm={handleBlockUnblockConfirm}
          title={isDeactivating ? "Deactivate User" : "Reactivate User"}
          description={
            isDeactivating
              ? `Deactivate "${
                  userToBlockUnblock?.first_name +
                  " " +
                  userToBlockUnblock?.last_name
                }"? This ends their session immediately and blocks them from logging in. Their records are kept, and they can be reactivated at any time.`
              : `Reactivate "${
                  userToBlockUnblock?.first_name +
                  " " +
                  userToBlockUnblock?.last_name
                }"? They will be able to log in again.`
          }
          itemName={
            userToBlockUnblock?.first_name +
            " " +
            userToBlockUnblock?.last_name
          }
          isLoading={deactivateUser.isPending || reactivateUser.isPending}
          destructive={isDeactivating}
          confirmLabel={isDeactivating ? "Deactivate" : "Reactivate"}
          loadingLabel={isDeactivating ? "Deactivating..." : "Reactivating..."}
        />

        {/* User Form Sheet (for creating new users only) */}
        <UserFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onCreate={onCreate}
          isLoading={createUser.isPending}
        />
      </div>
    </SidebarInset>
  );
}
